import { PlusOutlined } from '@ant-design/icons'
import { BlankIcon, EmptyState, PageHeader } from '@lmc/ui-kit'
import { Button } from '@lmc/ui-kit/src/general/Button'
import { LoadingScreen } from 'components/generic'
import { NAMESPACE } from 'locale'
import { goalsConstants } from 'locale/goals'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import GoalsList from './components/GoalsList/GoalsList'
import useFilteredGoals from './components/useGoals/useFilteredGoals'
import useResidents from 'hooks/useFindItem/useResidents'
import { isBrowser, isTablet } from 'react-device-detect'
import { UserRole } from '@lmc/ts-common'
import { useRoleAccess } from 'components/AccessControl'

const isDesktop = isBrowser || isTablet

const GoalsScreen = () => {
  const { id: residentID } = useParams()
  const navigate = useNavigate()

  const { result: resident } = useResidents({ id: residentID })

  const { t } = useTranslation(NAMESPACE.GOALS)
  const { goals, doneGoals, archivedGoals, isLoading } =
    useFilteredGoals(residentID)

  const residentName = `${resident?.name.first} ${resident?.name.last}`

  const hasRolePermission = useRoleAccess(
    [UserRole.careHomeAdmin, UserRole.seniorCarer],
    [
      {
        i: 'create',
        this: 'Goal',
      },
    ]
  )

  return (
    <>
      <PageHeader
        sticky
        title={`${residentName}'s ${t(goalsConstants.GOALS)}`}
        // This is a reused code snippet for the back button that's inside a header that's already inside the codebase. Might be a good idea to refactor it at some point
        onBack={
          !isDesktop
            ? () =>
                navigate(`/residents/${residentID}/profile`, { replace: true })
            : undefined
        }
      >
        <Link to="create" style={{ color: 'white' }}>
          <Button
            variant="primary"
            icon={<PlusOutlined />}
            data-cy="add-goal-btn"
            disabledByPermission={!hasRolePermission}
          >
            {t(goalsConstants.ADD_GOAL)}
          </Button>
        </Link>
      </PageHeader>
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <ListsWrapper data-cy="goals-list-container">
          {goals.length > 0 ? (
            <GoalsList residentID={residentID} items={goals} />
          ) : (
            <EmptyState
              title={t(goalsConstants.GOALS_EMPTY_LIST_TITLE)}
              description={t(goalsConstants.GOALS_EMPTY_LIST_DESCRIPTION)}
              icon={<BlankIcon />}
            />
          )}
          <SecondaryGoalsLists>
            {doneGoals.length > 0 && (
              <GoalsList
                residentID={residentID}
                items={doneGoals}
                title={t(goalsConstants.COMPLETED)}
              />
            )}
            {archivedGoals.length > 0 && (
              <GoalsList
                residentID={residentID}
                items={archivedGoals}
                title={t(goalsConstants.ARCHIVED)}
              />
            )}
          </SecondaryGoalsLists>
        </ListsWrapper>
      )}
    </>
  )
}

const ListsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 80px;
  margin-top: 12px;
`

const SecondaryGoalsLists = styled(ListsWrapper)`
  gap: 60px;
`

export default GoalsScreen
